import qs from 'qs';
import type { FetchOptions, FetchResponse } from 'ofetch';
import type { $Fetch } from 'nitropack';

import type { RefreshTokenRequest } from '@@/modules/auth/runtime/types';
import { AuthGrantTypes, AUTH_FORM_URL_ENCODED_CONTENT_TYPE, AuthSessionEventTypes } from '@/constants/auth';
import { HttpStatus } from '@/constants/http-status';
import { publishAuthSessionEvent } from '@/utils/auth-session-channel';
import type { ApiFetchOptions } from '@/types/common';

type RefreshTokenResponseData = { access_token: string; refresh_token: string; expires_in: number };
type CustomError = { error: string; error_description?: string };
type RefreshTokenFetchError = {
    message?: string;
    response?: { status?: number; statusText?: string; _data?: unknown };
};

type RefreshResult = 'success' | 'permanent-failure' | 'transient-failure';

const ApiFetchAuthValues = Object.freeze({
    OfflineAccessScope: 'offline_access',
    InvalidGrantError: 'invalid_grant',
    InvalidTokenError: 'invalid_token',
    ExpiredTokenError: 'expired_token'
});

const FETCH_ERROR_STATUS_MESSAGE = 'Fetch Error';
const BEARER_PREFIX = 'Bearer ';
const isCustomError = (data: unknown): data is CustomError =>
    typeof data === 'object' && data !== null && 'error' in data;

export default defineNuxtPlugin(() => {
    const authBaseURL = useRuntimeConfig().public.authBaseURL;
    const authOptions = useRuntimeConfig().public.auth;

    const auth = useAuth();
    const router = useRouter();
    const userSession = useUserSessionStore();
    let refreshPromise: Promise<RefreshResult> | undefined;
    let signOutPromise: Promise<void> | undefined;

    const $apiFetch: $Fetch = $fetch.create({
        onRequest(context) {
            setRequestHeaders(context.options);
        },

        async onResponse(context) {
            if (isInvalidToken(context.response)) {
                const refreshResult = await refreshToken(context.options.headers);

                if (refreshResult === 'success') {
                    //set headers and retry the original request
                    setRequestHeaders(context.options);
                    handleMultipartHeaders(context.options);

                    await $fetch<unknown>(
                        context.request as any,
                        {
                            ...(context.options as ApiFetchOptions),
                            onResponse(ctx: any) {
                                //rewrite the response
                                Object.assign(context, ctx);
                            }
                        } as any
                    );

                    return;
                }

                if (refreshResult === 'permanent-failure') {
                    await signOutOnce();
                }
            }
        },

        onRequestError(context) {
            if (!context.error) {
                return;
            }

            throw createError({
                statusCode: HttpStatus.ServiceUnavailable,
                statusMessage: FETCH_ERROR_STATUS_MESSAGE,
                data: context.error.message,
                fatal: true
            });
        },

        async onResponseError({ response }) {
            if (!response) {
                return;
            }

            const rawError = collectErrorsFromResponse(response);
            throw createError({
                statusCode: response.status,
                statusMessage: response.statusText || FETCH_ERROR_STATUS_MESSAGE,
                data: rawError,
                fatal: response.status !== HttpStatus.BadRequest && response.status !== HttpStatus.Conflict
            });
        }
    });

    async function refreshToken(headers?: HeadersInit): Promise<RefreshResult> {
        const authorization = new Headers(headers).get('Authorization');
        const rejectedAccessToken = authorization?.startsWith(BEARER_PREFIX)
            ? authorization.slice(BEARER_PREFIX.length)
            : undefined;

        refreshPromise ??= refreshTokenWithLock(rejectedAccessToken).finally(() => {
            refreshPromise = undefined;
        });

        const refreshResult = await refreshPromise;

        return refreshResult === 'success' || syncSessionIfAccessTokenChanged(rejectedAccessToken)
            ? 'success'
            : refreshResult;
    }

    async function refreshAuthToken(): Promise<boolean> {
        if (!userSession.accessToken) {
            return false;
        }

        const headers = new Headers();
        setHeader(headers, 'Authorization', BEARER_PREFIX + userSession.accessToken);

        const refreshResult = await refreshToken(headers);

        return refreshResult === 'success';
    }

    async function refreshTokenWithLock(rejectedAccessToken?: string): Promise<RefreshResult> {
        if (!authOptions.scope.includes(ApiFetchAuthValues.OfflineAccessScope)) {
            return 'permanent-failure';
        }

        if (import.meta.client && navigator.locks) {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 35_000);

            try {
                return await navigator.locks.request(
                    'estimator-auth-token-refresh',
                    { signal: controller.signal },
                    () => refreshTokenUnderLock(rejectedAccessToken)
                );
            } catch {
                return 'transient-failure';
            } finally {
                clearTimeout(timeout);
            }
        }

        return refreshTokenUnderLock(rejectedAccessToken);
    }

    async function refreshTokenUnderLock(rejectedAccessToken?: string): Promise<RefreshResult> {
        if (syncSessionIfAccessTokenChanged(rejectedAccessToken)) {
            return 'success';
        }

        const currentAccessToken = userSession.accessToken;
        const currentRefreshToken = userSession.refreshToken;
        if (!currentAccessToken || !currentRefreshToken) {
            return 'permanent-failure';
        }

        const data: RefreshTokenRequest = {
            client_id: authOptions.clientId,
            grant_type: AuthGrantTypes.RefreshToken,
            refresh_token: currentRefreshToken
        };

        try {
            const response = await $fetch<RefreshTokenResponseData>(authOptions.tokenURL, {
                body: qs.stringify(data),
                headers: { 'Content-Type': AUTH_FORM_URL_ENCODED_CONTENT_TYPE },
                baseURL: authBaseURL,
                method: 'POST',
                timeout: 30_000
            });

            userSession.init();

            // A login, logout or another refresh may have completed while this request was in flight.
            // Never let its stale response overwrite or resurrect that newer shared browser session.
            if (!userSession.accessToken || !userSession.refreshToken) {
                return 'transient-failure';
            }

            if (userSession.accessToken !== currentAccessToken || userSession.refreshToken !== currentRefreshToken) {
                return 'success';
            }

            userSession.setTokens(response.access_token, response.refresh_token, response.expires_in);
            publishAuthSessionEvent(AuthSessionEventTypes.TokensUpdated);

            return 'success';
        } catch (error) {
            if (syncSessionIfAccessTokenChanged(rejectedAccessToken)) {
                return 'success';
            }

            const refreshError = error as RefreshTokenFetchError;
            const responseError = refreshError.response?._data;

            return refreshError.response?.status === HttpStatus.BadRequest &&
                isCustomError(responseError) &&
                responseError.error === ApiFetchAuthValues.InvalidGrantError
                ? 'permanent-failure'
                : 'transient-failure';
        }
    }

    function syncSessionIfAccessTokenChanged(rejectedAccessToken?: string): boolean {
        userSession.init();

        return Boolean(userSession.accessToken && userSession.accessToken !== rejectedAccessToken);
    }

    function signOutOnce(): Promise<void> {
        // The refresh token is already unusable. Calling the protected logout endpoint here
        // could recursively enter this interceptor and make sign-out wait for itself.
        signOutPromise ??= auth
            .signOut({ revokeRemoteSession: false, redirect: router.currentRoute.value.fullPath })
            .finally(() => {
                signOutPromise = undefined;
            });

        return signOutPromise;
    }

    function isInvalidToken(response: FetchResponse<unknown>) {
        if (!response?.headers) {
            return false;
        }

        const status = response.status;
        const wwwAuthenticateHeader = response.headers.get('www-authenticate');

        return (
            status === HttpStatus.Unauthorized &&
            wwwAuthenticateHeader &&
            (wwwAuthenticateHeader.includes(ApiFetchAuthValues.InvalidTokenError) ||
                wwwAuthenticateHeader.includes(ApiFetchAuthValues.ExpiredTokenError))
        );
    }

    function setRequestHeaders(options: FetchOptions) {
        //normalize headers
        options.headers = options.headers instanceof Headers ? options.headers : new Headers(options.headers);

        if (userSession.accessToken) {
            setHeader(options.headers, 'Authorization', BEARER_PREFIX + userSession.accessToken);
        }
    }

    function handleMultipartHeaders(options: FetchOptions) {
        if (options.body && typeof FormData !== 'undefined' && options.body instanceof FormData) {
            // Remove any 'Content-Type' header set by the user
            if (options.headers instanceof Headers) {
                options.headers.delete('Content-Type');
            } else if (options.headers && typeof options.headers === 'object' && !Array.isArray(options.headers)) {
                delete (options.headers as Record<string, string>)['Content-Type'];
                delete (options.headers as Record<string, string>)['content-type'];
            }
        }
    }

    function setHeader(headers: Headers | undefined, name: string, value?: string) {
        if (!headers || !value) {
            return;
        }

        headers.set(name, value);
    }

    function collectErrorsFromResponse(response: FetchResponse<unknown>) {
        const data = response._data;

        if (containsCustomErrors(data)) {
            const errors = Array.isArray(data) ? data : [data];

            return errors.map((e) => e.error_description || e.error);
        }

        return response.statusText;
    }

    function containsCustomErrors(data?: unknown): data is CustomError | CustomError[] {
        if (!data) {
            return false;
        }

        if (Array.isArray(data)) {
            //if array is array of custom errors
            return data.every(isCustomError);
        }

        //if single error
        return isCustomError(data);
    }

    return {
        provide: {
            apiFetch: $apiFetch,
            refreshAuthToken
        }
    };
});

declare module '#app' {
    interface NuxtApp {
        $apiFetch: $Fetch;
        $refreshAuthToken: () => Promise<boolean>;
    }
}
